import React, { useState } from "react";
import { Button, Col, Collapse, Row, Space, Table } from "antd";
import NumberFormat from "react-number-format";
import { clsx } from "clsx";
import { SvgIcon } from "../../../../common/components/SvgIcon";
import { Sanitized } from "../../../../common/components/Sanitized";
import { SummarySection } from "../../../../types/ContentTypes";
import {
    FeedbackCreation,
    NegotiationStrategy,
    ReportFeedback,
    StrategyWithOpportunity,
    TotalOpportunity,
} from "../../../../types/ReportTypes";
import { NegotiationStrategyModal } from "./NegotiationStrategyModal";
import { TotalOpportunityModal } from "./TotalOpportunityModal";
import { HtmlSummaryEditModal } from "./HtmlSummaryEditModal";
import { Chapter } from "./Outline";
import { SectionFeedback } from "./SectionFeedback";
import styles from "./NegotiationStrategySummary.module.less";

const { Panel } = Collapse;

const savingsRender = (value: number, row: StrategyWithOpportunity) => {
    return row.isPercentage ? (
        `${value}%`
    ) : (
        <NumberFormat value={value} thousandSeparator={true} displayType="text" prefix={"$"} />
    );
};

interface NegotiationStrategySummaryProps {
    title: string;
    description: string;
    html: string;
    sections?: SummarySection[];
    onUpdate?: (summary: string) => void;
    strategies?: StrategyWithOpportunity[];
    onChange?: (strategy: NegotiationStrategy) => void;
    totalOpportunity?: TotalOpportunity;
    onOpportunityChange?: (data: TotalOpportunity) => void;
    editable?: boolean;
    panelKey: Chapter;
    activeKeys?: string[];
    onActiveKeyChange?: (activeKeys: Chapter[]) => void;
    feedback?: ReportFeedback[];
    onFeedbackAdd?: (feedback: FeedbackCreation) => Promise<void>;
    onFeedbackRemove?: (feedbackId?: number) => Promise<void>;
}

export const NegotiationStrategySummary: React.FC<NegotiationStrategySummaryProps> = ({
    title,
    description,
    strategies,
    html,
    sections,
    onUpdate,
    onChange,
    totalOpportunity,
    onOpportunityChange,
    editable = false,
    panelKey,
    activeKeys,
    onActiveKeyChange,
    feedback,
    onFeedbackAdd,
    onFeedbackRemove,
}) => {
    const [showEdit, setShowEdit] = useState(false);
    const [showStrategyModal, setShowStrategyModal] = useState(false);
    const [toEdit, setToEdit] = useState<NegotiationStrategy>();
    const [showOpportunityModal, setShowOpportunityModal] = useState(false);

    const opportunitySectionName = "Summary of leverage points and potential savings";

    const hint = totalOpportunity?.opportunityHint ? ` - ${totalOpportunity?.opportunityHint}` : "";
    const dataSource = strategies?.concat([
        {
            name: "Total opportunity ($)" + hint,
            minSavings: totalOpportunity?.minTotalOpportunity || 0,
            maxSavings: totalOpportunity?.maxTotalOpportunity || 0,
            isOpportunity: true,
            rowClass: styles.opportunity,
        } as StrategyWithOpportunity,
        {
            name: "Total opportunity (%)",
            minSavings: totalOpportunity?.minTotalOpportunityPercentage || 0,
            maxSavings: totalOpportunity?.maxTotalOpportunityPercentage || 0,
            isOpportunity: true,
            isPercentage: true,
            rowClass: styles.opportunityPercentage,
        } as StrategyWithOpportunity,
        ...(totalOpportunity?.minTargetSavings
            ? [
                  {
                      name: "Target realized savings ",
                      minSavingsText: totalOpportunity?.minTargetSavings,
                      maxSavingsText: totalOpportunity?.maxTargetSavings,
                      isOpportunity: true,
                      isPercentage: false,
                      rowClass: styles.opportunity,
                  } as unknown as StrategyWithOpportunity,
              ]
            : []),
    ]);

    const handleEditClick = (row: StrategyWithOpportunity) => {
        if (row.isOpportunity) {
            setShowOpportunityModal(true);
            return;
        }
        setToEdit(row);
        setShowStrategyModal(true);
    };

    const handleAddClick = () => {
        setToEdit({} as StrategyWithOpportunity);
        setShowStrategyModal(true);
    };

    const editButton = editable ? (
        <Col>
            <Space>
                <Button
                    className={styles.edit}
                    type="link"
                    onClick={(e) => {
                        setShowEdit(true);
                        e.stopPropagation();
                    }}
                >
                    Edit
                </Button>
            </Space>
        </Col>
    ) : null;

    const actionColumn = editable
        ? [
              {
                  title: (
                      <Button type="link" size="small" onClick={handleAddClick}>
                          + Add
                      </Button>
                  ),
                  key: "actioin",
                  width: 82,
                  render: (_: any, row: NegotiationStrategy) => {
                      return (
                          <Button type="link" size="small" onClick={() => handleEditClick(row)}>
                              Edit
                          </Button>
                      );
                  },
              },
          ]
        : [];

    const showContent = Boolean(strategies?.length);

    return (
        <>
            <HtmlSummaryEditModal
                open={showEdit}
                title={title}
                summary={html}
                onOk={(value) => {
                    onUpdate?.(value);
                    setShowEdit(false);
                }}
                onCancel={() => setShowEdit(false)}
                width={1200}
            />
            <NegotiationStrategyModal
                open={showStrategyModal}
                strategy={toEdit}
                title={title}
                width={1200}
                onCancel={() => setShowStrategyModal(false)}
                onOk={(data) => {
                    onChange?.(data);
                    setShowStrategyModal(false);
                }}
            />
            <TotalOpportunityModal
                open={showOpportunityModal}
                data={totalOpportunity}
                title="Total opportunity"
                onCancel={() => setShowOpportunityModal(false)}
                onOk={(data) => {
                    onOpportunityChange?.(data);
                    setShowOpportunityModal(false);
                }}
            />
            <Collapse
                className={styles.summary}
                bordered={false}
                collapsible="header"
                expandIconPosition="end"
                expandIcon={() => <SvgIcon name="down" size={20} className={styles.expandIcon} />}
                activeKey={activeKeys}
                onChange={(value) => {
                    onActiveKeyChange?.(value as Chapter[]);
                }}
            >
                <Panel
                    header={
                        <Row align="middle">
                            <Col flex={1}>
                                <div className={styles.headerTitle}>{title}</div>
                                <div>{description}</div>
                            </Col>
                            {editButton}
                        </Row>
                    }
                    key={panelKey}
                >
                    {showContent ? (
                        <div className={`${styles.leverageSummary} ${styles.section}`}>
                            <div className={styles.sectionTitle}>{opportunitySectionName}</div>
                            <Table
                                dataSource={dataSource}
                                className={styles.strategyTable}
                                size="small"
                                pagination={false}
                                rowClassName={(item) => item.rowClass || ""}
                                columns={[
                                    {
                                        title: "Leverage point",
                                        dataIndex: "name",
                                        key: "name",
                                        className: styles.leveragePoint,
                                        width: 320,
                                    },
                                    {
                                        title: "Negotiation ask",
                                        dataIndex: "ask",
                                        key: "ask",
                                    },
                                    {
                                        title: "Potential savings (Low end)",
                                        dataIndex: "minSavings",
                                        key: "minSavings",
                                        className: styles.lowEnd,
                                        width: 200,
                                        render: (value: number, row: NegotiationStrategy) => {
                                            if (row.minSavingsText) return row.minSavingsText;
                                            return savingsRender(value, row);
                                        },
                                    },
                                    {
                                        title: "Potential savings (High end)",
                                        dataIndex: "maxSavings",
                                        key: "maxSavings",
                                        className: styles.highEnd,
                                        width: 200,
                                        render: (value: number, row: NegotiationStrategy) => {
                                            if (row.maxSavingsText) return row.maxSavingsText;
                                            return savingsRender(value, row);
                                        },
                                    },
                                    ...actionColumn,
                                ]}
                            ></Table>
                            <SectionFeedback
                                className={styles.feedback}
                                feedback={feedback}
                                sectionIdentifier={`${panelKey}.0`}
                                sectionName={opportunitySectionName}
                                onFeedbackAdd={onFeedbackAdd}
                                onFeedbackRemove={onFeedbackRemove}
                            />
                        </div>
                    ) : null}
                    <>
                        {sections?.map((s, index) => (
                            <div
                                className={clsx(
                                    styles.section,
                                    "il-summary-wrapper il-summary-wrapper--negotiationStrategy"
                                )}
                                key={index}
                            >
                                <Sanitized className="il-summary" content={s.value} />
                                <SectionFeedback
                                    className={styles.feedback}
                                    feedback={feedback}
                                    sectionIdentifier={`${panelKey}.${index + 1}`}
                                    sectionName={s.key}
                                    onFeedbackAdd={onFeedbackAdd}
                                    onFeedbackRemove={onFeedbackRemove}
                                />
                            </div>
                        ))}
                    </>
                </Panel>
            </Collapse>
        </>
    );
};
